import IconCrypto from "app/components/Icon/components/IconCrypto/IconCrypto";
import LogoApplePay from "app/components/Logos/LogoApplePay";
import LogoBankWire from "app/components/Logos/LogoBankWire";
import LogoCreditCard from "app/components/Logos/LogoCreditCard";
import LogoGooglePay from "app/components/Logos/LogoGooglePay/LogoGooglePay";
import LogoInterac from "app/components/Logos/LogoInterac/LogoInterac";
import LogoNeteller from "app/components/Logos/LogoNeteller";
import LogoSkrill from "app/components/Logos/LogoSkrill";
import { getRoutePathForView } from "app/helpers/routerPathHelper";
import type { UserType } from "app/types/userType";
import { ResponsiveScreenSizes } from "app/utils/const";
import { get } from "lodash";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import Media from "react-media";
import { Link } from "react-router-dom";
import "./ManageFundingOptions.scss";

type Props = {
  gateways: Array<Record<string, any>>;
  user: UserType;
  onClickFundingHistory: (...args: Array<any>) => any;
};

function ManageFundingOptions(props: Props) {
  const [isSkrillDetailsSectionVisible, toggleSkrillDetailsVisibility] =
    useState(false);
  const [isNetellerDetailsSectionVisible, toggleNetellerDetailsVisibility] =
    useState(false);
  const [isBankWireDetailsSectionVisible, toggleBankWireDetailsVisibility] =
    useState(false);
  const [isCryptoSectionVisible, toggleCryptoDetailsVisibility] =
    useState(false);
  const [isCreditCardSectionVisible, toggleCreditCardVisibility] =
    useState(false);
  const [isInteracDetailsSectionVisible, toggleInteracDetailsSectionVisible] =
    useState(false);
  const [isApplePayDetailsSectionVisible, toggleApplePayDetailsSectionVisible] =
    useState(false);
  const [
    isGooglePayDetailsSectionVisible,
    toggleGooglePayDetailsSectionVisible,
  ] = useState(false);

  const onClickArrow = e => {
    switch (e.currentTarget.dataset.name) {
      case "skrill":
        toggleSkrillDetailsVisibility(!isSkrillDetailsSectionVisible);
        break;
      case "neteller":
        toggleNetellerDetailsVisibility(!isNetellerDetailsSectionVisible);
        break;
      case "bankwire":
        toggleBankWireDetailsVisibility(!isBankWireDetailsSectionVisible);
        break;
      case "aquapay":
        toggleCryptoDetailsVisibility(!isCryptoSectionVisible);
        break;
      case "nuvei":
        toggleCreditCardVisibility(!isCreditCardSectionVisible);
        break;
      case "interac":
        toggleInteracDetailsSectionVisible(!isInteracDetailsSectionVisible);
        break;
      case "google_pay":
        toggleGooglePayDetailsSectionVisible(!isGooglePayDetailsSectionVisible);
        break;

      case "apple_pay":
        toggleApplePayDetailsSectionVisible(!isApplePayDetailsSectionVisible);
        break;

      default:
        break;
    }
  };
  const { gateways, user, onClickFundingHistory } = props;

  return (
    <div className="manage-funding__content__options">
      {gateways.length > 0 && user["amsCanAccessCashier"] ? (
        gateways.map((gateway, index) => {
          let logo: JSX.Element | null = null;
          let sectionVisible: boolean = false;

          switch (gateway.type) {
            case "skrill":
              logo = <LogoSkrill className="option__skrill-logo" />;
              sectionVisible = isSkrillDetailsSectionVisible;
              break;

            case "neteller":
              logo = <LogoNeteller className="option__neteller-logo" />;
              sectionVisible = isNetellerDetailsSectionVisible;
              break;

            case "bankwire":
              logo = <LogoBankWire className="option__bank-transfer-logo" />;
              sectionVisible = isBankWireDetailsSectionVisible;
              break;

            case "aquapay":
              logo = (
                <span className="option__crypto-logo">
                  {"CryptoCurrency"}
                  <IconCrypto name="BTC" />
                  <IconCrypto name="USDT_ERC20" />
                  <IconCrypto name="ETH" />
                </span>
              );
              sectionVisible = isCryptoSectionVisible;
              break;

            case "nuvei":
              logo = <LogoCreditCard className="option__nuvei-logo" />;
              sectionVisible = isCreditCardSectionVisible;
              break;

            case "interac":
              logo = <LogoInterac />;
              sectionVisible = isInteracDetailsSectionVisible;
              break;

            case "apple_pay":
              logo = <LogoApplePay className="option__nuvei-logo" />;
              sectionVisible = isApplePayDetailsSectionVisible;
              break;

            case "google_pay":
              logo = <LogoGooglePay className="option__nuvei-logo" />;
              sectionVisible = isGooglePayDetailsSectionVisible;
              break;

            default:
              logo = <div />;
              break;
          }
          const depositMin = get(gateway, [
            "depositDetails",
            "txnLimits",
            "min",
          ]);
          const depositMax = get(gateway, [
            "depositDetails",
            "txnLimits",
            "max",
          ]);
          const depositFee = get(gateway, ["depositDetails", "fees", "pct"]);

          const withdrawMin = get(gateway, [
            "withdrawDetails",
            "txnLimits",
            "min",
          ]);
          const withdrawMax = get(gateway, [
            "withdrawDetails",
            "txnLimits",
            "max",
          ]);
          const withdrawFee = get(gateway, ["withdrawDetails", "fees", "pct"]);
          const path = getRoutePathForView();
          // add due to gateway and payment method naming inconsistency
          const link = `${path}${gateway.type}`;

          return (
            <div className={`payment-channels ${gateway.type}`} key={index}>
              <div className="payment-channels__header">
                <Link to={link} className="option">
                  {logo}
                </Link>
                <span
                  data-name={gateway.type}
                  onClick={onClickArrow}
                  className={`arrow  ${
                    sectionVisible ? "arrow--up" : "arrow--down"
                  }`}
                ></span>
              </div>
              <div
                className={`payment-channels__content ${
                  sectionVisible
                    ? "payment-channels__content--show"
                    : "payment-channels__content--hide"
                }`}
              >
                <div className="payment-channels__content__cols">
                  {withdrawMin != null && (
                    <span>
                      <FormattedMessage id="Min Withdrawal" />
                      {": "}
                      {withdrawMin}
                    </span>
                  )}
                  {withdrawMax != null && (
                    <span>
                      <FormattedMessage id="Max Withdrawal" />
                      {": "}
                      {withdrawMax}
                    </span>
                  )}
                  {withdrawFee != null && (
                    <span>
                      <FormattedMessage id="Withdrawal Fee" />
                      {`: ${withdrawFee * 100}%`}
                    </span>
                  )}
                </div>
                <div className="payment-channels__content__cols">
                  {depositMin != null && (
                    <span>
                      <FormattedMessage id="Min Deposit" />
                      {": "}
                      {depositMin}
                    </span>
                  )}
                  {depositMax != null && (
                    <span>
                      <FormattedMessage id="Max Deposit" />
                      {": "}
                      {depositMax}
                    </span>
                  )}
                  {depositFee != null && (
                    <span>
                      <FormattedMessage id="Deposit Fee" />
                      {`: ${depositFee * 100}%`}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="no-options">
          <FormattedMessage tagName="span" id="cashier-contact-ams-agent" />
        </div>
      )}

      <Media
        query={`(min-width: ${ResponsiveScreenSizes.desktop})`}
        render={() => (
          <div
            className="manage-funding__content__options"
            onClick={() => onClickFundingHistory()}
          >
            <div className="option">
              <FormattedMessage tagName="span" id="reports-FundingHistory" />
            </div>
          </div>
        )}
      />
    </div>
  );
}
export default ManageFundingOptions;
